import { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { LolerReportListItem, LolerReportFilter, LolerReportCreateInput, LolerDefectInput, LolerDefectCategory, ReportCompleteness, ExtractedReport, LolerGmailSweepResult } from "../../lib/api";
import { RecordPicker } from "../../components/RecordPicker";
import { useAuth } from "../../lib/auth";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { useSiteFilter, SiteFilterChip } from "./customerFilter";
import { LolerGmailSweepModal } from "./LolerGmailSweepModal";
import { LolerReviewList } from "./LolerReviewList";
import { buildIntakeFields, hasLeadGenDetails, type LolerIntakeMode } from "./lolerPreContract";
import { PreContractFields } from "./PreContractFields";
import { buildLiftSpecFields, EMPTY_LIFT_SPEC, type LiftSpecDetails } from "./lolerLiftSpec";
import { LiftSpecFields } from "./LiftSpecFields";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { ListToolbar } from "../../components/ListToolbar";
import { useListSearch } from "../../lib/useListSearch";
import { useServerPage } from "../../lib/useServerPage";
import { Pager } from "../../components/Pager";
import { ExportCsvMenu } from "../../components/ExportCsvMenu";
import { useSortableColumn } from "../../lib/useSortableColumn";
import { SortableHeader } from "../../components/SortableHeader";

const EMPTY = "-";

// Session SC2C: the list is 50 at a time; the SERVER filters (Open / Closed / All, in the database), searches and sorts it
// (GET /api/loler/reports, reportListQuery.ts). The sort columns are lift, customer, inspector, nextDue, report, items and received.
const LOLER_PAGE_SIZE = 50;
const SORT_OPTIONS = [
  { key: "lift", label: "Lift / site" },
  { key: "customer", label: "Customer" },
  { key: "nextDue", label: "Next exam due" },
  { key: "items", label: "Items" },
  { key: "received", label: "Received" },
];
const FILTERS: { value: LolerReportFilter; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
  { value: "all", label: "All" },
];
const CATEGORIES: { value: LolerDefectCategory; label: string }[] = [
  { value: "immediate", label: "Immediate danger" },
  { value: "timed", label: "Remedy by date" },
  { value: "advisory", label: "Advisory" },
];

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

export function LolerReportsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Session ADI: the site detail page links here with ?siteId= to show only that site's reports.
  // The server filters on the linked asset's site; the status filter defaults to "all" then so the
  // site's full examination history shows, not just the open ones.
  const { siteId, clear: clearSite } = useSiteFilter();
  // Card list below md, table otherwise -- decided once at mount (Session AHB).
  const [mobileCards] = useState(initialMobileLayout);
  const [filter, setFilter] = useState<LolerReportFilter>(siteId ? "all" : "open");
  const [showNew, setShowNew] = useState(false);

  // Session AGF: sweep the inbox for inspection-body LOLER report emails, AI-extract each PDF, and
  // present a review queue. Gated on Google being connected; the sweep itself writes nothing.
  const [sweeping, setSweeping] = useState(false);
  const [sweepResult, setSweepResult] = useState<LolerGmailSweepResult | null>(null);
  const [sweepError, setSweepError] = useState<string | null>(null);
  const [googleConnected, setGoogleConnected] = useState<boolean | null>(null);

  useEffect(() => {
    api.integrations
      .status()
      .then((s) => setGoogleConnected(s.google.connected))
      .catch(() => setGoogleConnected(false));
  }, []);

  async function sweepGmail() {
    setSweeping(true);
    setSweepError(null);
    try {
      setSweepResult(await api.loler.sweepGmail());
    } catch (e) {
      setSweepError(
        e instanceof ApiError
          ? e.code === "google_not_connected"
            ? "Google is not connected. Connect it in Settings, then try again."
            : e.code === "ai_not_configured"
              ? "The AI assistant is not configured, so the inbox cannot be read automatically."
              : e.message || "Could not sweep Gmail. Please try again in a moment."
          : "Could not sweep Gmail. Please try again in a moment.",
      );
    } finally {
      setSweeping(false);
    }
  }

  // Type-to-confirm delete (admin). A LOLER report has no UI delete on the list today, so the
  // Director could not remove a duplicate without opening each report's detail page (Session AB,
  // Item 6). The endpoint + guard already exist; this surfaces it on the row. The confirm value is
  // the visible lift/site label (a report's optional ref is not on the list DTO, and a duplicate
  // may carry none, so the label is the reliable thing to type).
  const [deleting, setDeleting] = useState<{ id: string; label: string } | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // M-10, Session AQU: search and sort. Session SC2C: asked of the server, which answers one page. The search is the one in the address bar
  // (the toolbar writes it there after a short pause). No active sort column keeps the server's order (newest received first).
  const { search, setSearch } = useListSearch();
  const [searchParams] = useSearchParams();
  const urlSearch = (searchParams.get("q") ?? "").trim();
  const sort = useSortableColumn("loler-reports");
  const queryKey = JSON.stringify({ filter, siteId, urlSearch, sk: sort.sortKey, sd: sort.sortDir });
  const page = useServerPage(queryKey, (at) =>
    api.loler.reportsPage({
      filter,
      ...(siteId ? { siteId } : {}),
      ...(urlSearch ? { q: urlSearch } : {}),
      ...(sort.sortKey ? { sort: sort.sortKey, dir: sort.sortDir } : {}),
      limit: LOLER_PAGE_SIZE,
      offset: at,
    }),
  );
  const loading = page.loading;
  const error = page.error;
  const shown: LolerReportListItem[] = page.data?.items ?? [];
  const total = page.data?.total ?? 0;

  async function confirmDelete() {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.loler.deleteReport(deleting.id);
      setDeleting(null);
      await page.reload();
    } catch (err) {
      // The server's 409 (actioned items) carries a plain-English message; show it in the dialog.
      setDeleteError(err instanceof ApiError ? err.message || err.code : "Could not delete the report. Please try again.");
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="LOLER"
        className="mb-4"
        subtitle={
          <span className="block max-w-2xl">
            Inbound thorough-examination reports from the duty holder's independent inspector. ELE acts on the defects:
            covered work is scheduled, chargeable work is quoted, and each item is tracked to closure.
          </span>
        }
        actions={
          <>
            <button
              onClick={sweepGmail}
              disabled={googleConnected === false || sweeping}
              title={googleConnected === false ? "Connect Google in Settings" : undefined}
              className="max-sm:min-h-11 rounded-ele border border-ele-blue px-5 py-2.5 font-heading tracking-wide text-ele-blue transition hover:bg-ele-blue/10 disabled:opacity-50"
            >
              {sweeping ? "Searching Gmail..." : "Sweep Gmail"}
            </button>
            <Button onClick={() => setShowNew(true)} icon={<Plus aria-hidden size={15} />}>
              New report
            </Button>
            {/* Session EXP1: the CSV of this list as it is showing (Open, Closed or All, the site, the search and the sort), in the More actions menu. */}
            <ExportCsvMenu
              list="loler-reports"
              params={{
                status: filter,
                ...(siteId ? { siteId } : {}),
                ...(urlSearch ? { q: urlSearch } : {}),
                ...(sort.sortKey ? { sort: sort.sortKey, dir: sort.sortDir } : {}),
              }}
            />
          </>
        }
      />

      {sweepError ? (
        <p role="alert" className="mb-4 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {sweepError}
        </p>
      ) : null}

      {/* Session BAD: reports found in the mailbox that were not filed on their own, waiting for a person. */}
      <LolerReviewList onChanged={() => void page.reload()} />

      {/* The Open / Closed / All buttons are a SERVER filter (they re-fetch), so the toolbar is
          always shown: hiding it on an empty list would take away the way to switch filter. */}
      <ListToolbar
        className="mb-4"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search lift, site, customer or inspector..."
        searchLabel="Search LOLER reports"
        sort={{ state: sort, options: SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
        onClear={() => setSearch("")}
        filters={FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`rounded-ele border px-4 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
              filter === f.value ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
            }`}
          >
            {f.label}
          </button>
        ))}
      />

      {siteId && (
        <div className="mb-4">
          <SiteFilterChip noun="LOLER reports" onClear={clearSite} />
        </div>
      )}

      {loading ? (
        <Loader label="Loading the LOLER reports..." />
      ) : error ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the reports. Please try again in a moment.
        </p>
      ) : shown.length === 0 ? (
        urlSearch ? (
          <EmptyState role="status" message={`No LOLER reports match "${urlSearch}".`} />
        ) : (
          <EmptyState message={`No ${filter === "all" ? "" : filter} reports.`} />
        )
      ) : mobileCards ? (
        /* Session AHB: the three columns the table keeps below md left the lift and site 106px,
           the next exam due 63px against ~72px of date, and the items count 70px with an
           overdue badge that (in an overflow-visible .cell-actions cell) painted over its
           neighbour rather than clipping. The customer and the inspector come back too. */
        <MobileCardList label="LOLER reports">
          {shown.map((r) => (
            <MobileListCard
              key={r.id}
              to={`/office/loler/${r.id}`}
              primary={<span className="font-heading">{r.ele ?? r.siteName ?? "Lift"}</span>}
              secondary={[r.ele ? r.siteName : null, r.customerName].filter(Boolean).join(" | ") || undefined}
              meta={`Next exam due ${formatDate(r.nextExaminationDue)}`}
              right={
                <>
                  <span className="text-sm">
                    <span className="text-ele-strong">{r.openCount}</span>
                    <span className="text-ele-body">/{r.totalCount} open</span>
                  </span>
                  {r.overdueCount > 0 && <StatusPill word={`${r.overdueCount} overdue`} tone="red" />}
                  {r.completeness !== "full" && <StatusPill word={r.completeness} tone="amber" />}
                  {!r.hasContract && <StatusPill word="Pre-contract" tone="amber" />}
                </>
              }
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: isAdmin ? "16%" : "20%" }} />
              <col className="max-md:hidden" style={{ width: isAdmin ? "16%" : "18%" }} />
              <col className="max-md:hidden" style={{ width: isAdmin ? "13%" : "15%" }} />
              <col style={{ width: "12%" }} />
              <col className="max-md:hidden" style={{ width: "10%" }} />
              <col style={{ width: isAdmin ? "13%" : "14%" }} />
              <col className="max-md:hidden" style={{ width: "11%" }} />
              {isAdmin && <col className="max-md:hidden" style={{ width: "9%" }} />}
              {/* Mobile-only disclosure chevron (Session ACW Loop 3): the row navigates to the
                  report detail page below md; Lift/site, Next exam due and Items (the open/overdue
                  defect count, the report's operational result) stay visible. */}
              <col className="w-8 md:hidden" />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Lift / site" colKey="lift" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Customer" colKey="customer" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Inspector" colKey="inspector" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Next exam due" colKey="nextDue" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Report" colKey="report" sort={sort} onToggle={sort.toggle} className="cell-actions max-md:hidden" />
                <SortableHeader label="Items" colKey="items" sort={sort} onToggle={sort.toggle} className="cell-actions" />
                <SortableHeader label="Received" colKey="received" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                {isAdmin && <th className="cell-actions max-md:hidden px-2 py-2 text-right">Actions</th>}
                <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} className="group cursor-pointer border-t border-ele-purple-accent hover:bg-ele-surface" onClick={() => navigate(`/office/loler/${r.id}`)}>
                  <td className="cell-actions px-3 py-2 align-top">
                    <span className="font-heading text-ele-strong">{r.ele ?? r.siteName ?? "Lift"}</span>
                    {r.ele && r.siteName ? <span className="block text-xs text-ele-body">{r.siteName}</span> : null}
                  </td>
                  <td className="cell-actions max-md:hidden px-3 py-2 align-top text-ele-body">{r.customerName ?? EMPTY}{!r.hasContract ? <StatusPill className="ml-1" word="Pre-contract" tone="amber" /> : null}</td>
                  <td className="max-md:hidden px-3 py-2 align-top text-ele-body">{r.inspectorName ?? EMPTY}</td>
                  <td className="px-3 py-2 align-top text-ele-body">{formatDate(r.nextExaminationDue)}</td>
                  <td className="cell-actions max-md:hidden px-3 py-2 align-top">
                    <StatusPill word={r.completeness} tone={r.completeness === "full" ? "green" : "amber"} />
                  </td>
                  <td className="cell-actions px-3 py-2 align-top">
                    <span className="text-ele-strong">{r.openCount}</span>
                    <span className="text-ele-body">/{r.totalCount} open</span>
                    {r.overdueCount > 0 ? <StatusPill className="ml-1" word={`${r.overdueCount} overdue`} tone="red" /> : null}
                  </td>
                  <td className="max-md:hidden px-3 py-2 align-top text-ele-body">{formatDate(r.receivedAt)}</td>
                  {isAdmin && (
                    <td className="cell-actions max-md:hidden px-2 py-2 text-right align-top">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation(); // do not navigate to the detail page
                          setDeleteError(null);
                          setDeleting({ id: r.id, label: r.ele ?? r.siteName ?? "this report" });
                        }}
                        className="font-menu text-xs tracking-wide text-ele-danger transition hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  )}
                  <td className="cell-actions md:hidden pr-1 text-right">
                    <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                      <path d="M6 4l4 4-4 4z" />
                    </svg>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Session SC2C: 50 at a time; the total is the server's count of every report matching the filter and search. */}
      {!loading && !error && (
        <div className={page.refreshing ? "opacity-60" : undefined}>
          <Pager offset={page.offset} total={total} shown={shown.length} pageSize={LOLER_PAGE_SIZE} onOffset={page.setOffset} noun={["report", "reports"]} />
        </div>
      )}

      {showNew ? <NewReportModal onClose={() => setShowNew(false)} onCreated={(id) => navigate(`/office/loler/${id}`)} /> : null}

      {sweepResult ? (
        <LolerGmailSweepModal
          result={sweepResult}
          onClose={() => {
            setSweepResult(null);
            void page.reload(); // new reports may have been created during the sweep review
          }}
        />
      ) : null}

      {deleting && (
        <ConfirmDeleteModal
          open
          title="Delete LOLER report?"
          message="This permanently deletes the report and its defect items. A report with an actioned item (a confirmed treatment, a remedial quote or a scheduled job) is blocked. The filed report PDF in Drive is kept."
          confirmLabel="Delete report"
          busy={deleteBusy}
          error={deleteError}
          onConfirm={confirmDelete}
          onCancel={() => {
            if (!deleteBusy) {
              setDeleting(null);
              setDeleteError(null);
            }
          }}
        />
      )}
    </div>
  );
}

function NewReportModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [mode, setMode] = useState<LolerIntakeMode>("lift");
  const [form, setForm] = useState({
    customerId: "",
    siteId: "",
    assetId: "",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    siteName: "",
    siteAddress: "",
    inspectorName: "",
    examinationDate: "",
    nextExaminationDue: "",
    reportRef: "",
    completeness: "full" as ReportCompleteness,
    notes: "",
  });
  const [defects, setDefects] = useState<LolerDefectInput[]>([]);
  // The lift's own technical specification, as printed on the report (Session ALO, LOLER spec Items
  // 1/2): the same fields the Director already reads off every report by hand to build a maintenance
  // quote. Set on either branch -- a fact about the examination, not about who or what it identifies.
  const [liftSpec, setLiftSpec] = useState<LiftSpecDetails>(EMPTY_LIFT_SPEC);
  // The extraction reads customer/site details that ONLY the pre-contract branch saves, and
  // the form defaults to "lift". Without this the office is never told the report names a
  // non-customer, so those fields are silently dropped on Create (spec section 4, the
  // lead-gen case). A HINT, never an automatic mode switch: an existing customer's report
  // names a customer too, so which branch is right is the office's call.
  const [leadGenHint, setLeadGenHint] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Front-of-flow PDF: held until the report is created, then filed (Item 3 routes it to
  // the per-lift subfolder). Uploading runs the AI extraction and pre-fills the form.
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractMsg, setExtractMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Session SC2A: the customer, site and lift are three typeahead pickers (each narrowed by the one before), never preloaded registers.

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  // Pre-fill the form from an AI extraction (a DRAFT the user confirms/edits). Only fills
  // a field the model found; never clears one the user already typed.
  function applyExtraction(res: ExtractedReport) {
    const h = res.header ?? {};
    setForm((f) => ({
      ...f,
      customerName: h.customerName ?? f.customerName,
      customerEmail: h.customerEmail ?? f.customerEmail,
      customerPhone: h.customerPhone ?? f.customerPhone,
      siteName: h.siteName ?? f.siteName,
      siteAddress: h.siteAddress ?? f.siteAddress,
      inspectorName: h.inspectorName ?? f.inspectorName,
      examinationDate: h.examinationDate ?? f.examinationDate,
      nextExaminationDue: h.nextExaminationDue ?? f.nextExaminationDue,
      reportRef: h.reportRef ?? f.reportRef,
      completeness: h.completeness ?? f.completeness,
    }));
    // The lift's own technical specification: only a field the model actually found overwrites what
    // the office already typed, matching the header fields above.
    setLiftSpec((f) => ({
      liftType: h.liftType ?? f.liftType,
      liftSerialNumber: h.liftSerialNumber ?? f.liftSerialNumber,
      liftFloors: h.liftFloors != null ? String(h.liftFloors) : f.liftFloors,
      liftInstallYear: h.liftInstallYear != null ? String(h.liftInstallYear) : f.liftInstallYear,
      liftAgeYears: h.liftAgeYears != null ? String(h.liftAgeYears) : f.liftAgeYears,
    }));
    if (res.defects.length) {
      setDefects((d) => [...d, ...res.defects.map((x) => ({ description: x.description, category: x.category, deadline: (x.deadline ?? null) as string | null }))]);
    }
    // The same predicate the Gmail sweep uses to decide whether to raise its own pre-contract note,
    // so the two surfaces cannot come to different views of what "the report names an enquirer" means.
    if (hasLeadGenDetails(h)) setLeadGenHint(true);
  }

  // Hold the PDF (filed on create) and run the AI extraction to pre-fill the form. A
  // failure (AI off, unreadable) is non-fatal: the file is kept and the office fills the
  // form by hand.
  async function onPickPdf(file: File) {
    setPdfFile(file);
    setExtractMsg(null);
    setExtracting(true);
    try {
      const res = await api.loler.extractUpload(file);
      applyExtraction(res);
      const found = res.defects?.length ?? 0;
      setExtractMsg(`Pre-filled from the PDF. ${found} defect${found === 1 ? "" : "s"} found. Check and edit before creating.`);
    } catch (e) {
      setExtractMsg(
        e instanceof ApiError && e.status === 503
          ? "The AI assistant is not configured, so the form was not pre-filled. Enter the details manually. The PDF will still be filed."
          : "Could not read the PDF automatically. Enter the details manually. The PDF will still be filed.",
      );
    } finally {
      setExtracting(false);
    }
  }

  async function submit() {
    setSaving(true);
    setErr(null);
    const body: LolerReportCreateInput = {
      inspectorName: form.inspectorName || undefined,
      examinationDate: form.examinationDate || undefined,
      nextExaminationDue: form.nextExaminationDue || undefined,
      reportRef: form.reportRef || undefined,
      completeness: form.completeness,
      notes: form.notes || undefined,
      defects: defects.filter((d) => d.description.trim()),
      ...buildLiftSpecFields(liftSpec),
    };
    // Which branch this report is filed on, and therefore which fields the create carries, is decided
    // in ONE place shared with the Gmail sweep (lolerPreContract.ts). The pre-contract fields are
    // saved by that branch alone, so a surface that builds its own body is how one of them goes
    // missing without anything failing (customerPhone, Session ALG).
    const intake = buildIntakeFields(mode, {
      assetId: form.assetId,
      details: {
        customerName: form.customerName,
        customerEmail: form.customerEmail,
        customerPhone: form.customerPhone,
        siteName: form.siteName,
        siteAddress: form.siteAddress,
      },
    });
    if (!intake.ok) {
      setErr(intake.message);
      setSaving(false);
      return;
    }
    Object.assign(body, intake.fields);
    try {
      const created = await api.loler.createReport(body);
      // File the uploaded PDF against the new report (Item 3 routes it to the per-lift
      // subfolder). Non-fatal: if filing fails (e.g. Drive not connected) the report still
      // exists and the PDF can be uploaded from the report page.
      if (pdfFile) {
        try {
          await api.loler.uploadReportPdf(created.id, pdfFile);
        } catch {
          /* report created; PDF can be re-uploaded on the report page */
        }
      }
      onCreated(created.id);
    } catch (e) {
      // Matches the Gmail sweep card's own create() catch (LolerGmailSweepModal.tsx): a readable
      // e.message first, e.code only as the last resort when the server gave no wording at all,
      // never the bare code alone as the primary message.
      setErr(e instanceof ApiError ? e.message || e.code : "Could not create the report.");
      setSaving(false);
    }
  }

  return (
    <ModalShell ariaLabel="New LOLER report" onClose={() => !saving && onClose()} width="max-w-[560px]" margin="my-8" panelClassName="p-6">
        <h2 className="mb-3 font-heading text-2xl tracking-wide">New LOLER report</h2>

        {/* Front-of-flow: upload the third-party report PDF -> AI pre-fills the form. */}
        <div className="mb-4 rounded-ele border border-ele-blue/40 bg-ele-surface p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-menu text-xs uppercase tracking-wide text-ele-blue">Start from the report PDF (optional)</p>
              <p className="text-xs text-ele-body">Upload the inspector's LOLER report and we pre-fill the form for you to confirm. The PDF is filed against the report.</p>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              aria-label="Upload report PDF"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onPickPdf(f);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              disabled={extracting}
              onClick={() => fileRef.current?.click()}
              className="rounded-ele border border-ele-blue px-4 py-1.5 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-blue/10 disabled:opacity-50"
            >
              {extracting ? "Reading..." : pdfFile ? "Replace PDF" : "Upload report PDF"}
            </button>
          </div>
          {pdfFile ? <p className="mt-2 text-xs text-ele-body">Attached: {pdfFile.name}</p> : null}
          {extractMsg ? <p className="mt-1 text-xs text-amber-400">{extractMsg}</p> : null}
          {leadGenHint && mode === "lift" && !form.assetId ? (
            <div className="mt-2 rounded-ele border border-amber-500/40 bg-amber-500/10 p-2">
              <p className="text-xs text-amber-400">
                The report also names a customer and site. If this lift is not on file yet, switch to a pre-contract enquiry so those details are saved
                with the report. Choosing a lift above keeps this as a normal report and hides this note.
              </p>
              <button
                type="button"
                onClick={() => {
                  setMode("prospect");
                  setLeadGenHint(false);
                }}
                className="mt-1 font-menu text-xs tracking-wide text-amber-400 underline"
              >
                Switch to pre-contract enquiry
              </button>
            </div>
          ) : null}
        </div>

        <div className="mb-4 flex gap-2">
          {(["lift", "prospect"] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`rounded-ele border px-4 py-1.5 font-menu text-xs tracking-wide max-sm:min-h-11 ${mode === m ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body"}`}
            >
              {m === "lift" ? "Existing lift" : "Pre-contract enquiry"}
            </button>
          ))}
        </div>

        {mode === "lift" ? (
          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <RecordPicker kind="customer" label="Customer" value={form.customerId} placeholder="Search for a customer" onChange={(id) => setForm((f) => ({ ...f, customerId: id, siteId: "", assetId: "" }))} />
            </div>
            <div>
              <RecordPicker
                kind="site"
                label="Site"
                value={form.siteId}
                placeholder={form.customerId ? "Search this customer's sites" : "Choose a customer first"}
                disabled={!form.customerId}
                filters={{ customerId: form.customerId }}
                onChange={(id) => setForm((f) => ({ ...f, siteId: id, assetId: "" }))}
              />
            </div>
            <div>
              <RecordPicker
                kind="lift"
                label="Lift"
                value={form.assetId}
                placeholder={form.siteId ? "Search this site's lifts" : "Choose a site first"}
                disabled={!form.siteId}
                filters={{ siteId: form.siteId }}
                onChange={(id) => set("assetId", id)}
              />
            </div>
          </div>
        ) : (
          <PreContractFields
            className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2"
            idPrefix="new-report"
            labelCls={labelCls}
            inputCls={inputCls}
            value={{
              customerName: form.customerName,
              customerEmail: form.customerEmail,
              customerPhone: form.customerPhone,
              siteName: form.siteName,
              siteAddress: form.siteAddress,
            }}
            onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
          />
        )}

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div><label className={labelCls}>Inspector / inspectorate</label><input className={inputCls} value={form.inspectorName} onChange={(e) => set("inspectorName", e.target.value)} placeholder="e.g. ELTI, Allianz" /></div>
          <div><label className={labelCls}>Examination date</label><input type="date" className={inputCls} value={form.examinationDate} onChange={(e) => set("examinationDate", e.target.value)} /></div>
          <div><label className={labelCls}>Next exam due</label><input type="date" className={inputCls} value={form.nextExaminationDue} onChange={(e) => set("nextExaminationDue", e.target.value)} /></div>
          <div><label className={labelCls}>Report reference</label><input className={inputCls} value={form.reportRef} onChange={(e) => set("reportRef", e.target.value)} /></div>
          <div>
            <label className={labelCls}>Completeness</label>
            <select className={inputCls} value={form.completeness} onChange={(e) => set("completeness", e.target.value as ReportCompleteness)}>
              <option value="full">Full report</option>
              <option value="partial">Partial extract</option>
            </select>
          </div>
        </div>

        <div className="mb-4">
          <p className={labelCls}>
            Lift specification (optional). Read off the report when stated: the same numbers a maintenance quote for this lift would need.
          </p>
          <LiftSpecFields idPrefix="new-report-spec" labelCls={labelCls} inputCls={inputCls} value={liftSpec} onChange={(patch) => setLiftSpec((f) => ({ ...f, ...patch }))} />
        </div>

        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <label className={labelCls + " mb-0"}>Defects (one per line)</label>
            <button onClick={() => setDefects((d) => [...d, { description: "", category: "advisory" }])} className="font-menu text-xs tracking-wide text-ele-green hover:underline">+ Add defect</button>
          </div>
          {defects.length === 0 ? <p className="text-xs text-ele-body">Add the report's defects now, or add them on the report page.</p> : null}
          <div className="space-y-2">
            {defects.map((d, i) => (
              <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_160px_140px_auto]">
                <input className={inputCls} placeholder="Defect description" value={d.description} onChange={(e) => setDefects((arr) => arr.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} />
                <select className={inputCls} value={d.category} onChange={(e) => setDefects((arr) => arr.map((x, j) => (j === i ? { ...x, category: e.target.value as LolerDefectCategory } : x)))}>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <input type="date" className={inputCls} value={d.deadline ?? ""} onChange={(e) => setDefects((arr) => arr.map((x, j) => (j === i ? { ...x, deadline: e.target.value || null } : x)))} />
                <button onClick={() => setDefects((arr) => arr.filter((_, j) => j !== i))} className="rounded-ele border border-ele-purple-accent px-3 text-ele-body hover:text-ele-danger">Remove</button>
              </div>
            ))}
          </div>
        </div>

        {err ? <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">{err}</p> : null}

        <div className={`gap-2 ${MODAL_ACTIONS}`}>
          <button onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>Cancel</button>
          <Button onClick={submit} disabled={saving} className={MODAL_ACTION_BTN}>
            {saving ? "Creating..." : "Create report"}
          </Button>
        </div>
    </ModalShell>
  );
}
